import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { filter, take } from 'rxjs/operators';
import {
  IgxCategoryChartModule,
  IgxPieChartModule,
  IgxItemLegendModule,
  IgxLegendModule,
} from 'igniteui-angular-charts';
import { ReportsIntergrationService } from '../../services/reports-intergration.service';
import {
  CompletionTableData,
  MAndEData,
  MandEConfigModel,
  MoodAnalyticsData,
  QuizPerformanceResponse,
} from '../../models/studentProfileModels';
import { KeycloakAuthService } from '../../services/keycloak-auth.service';
import { HttpHelperService } from '../../services/helper.service';
import { DateRangeDialogComponent } from '../../date-range-dialog/date-range-dialog.component';
import { ReportsBaseService } from '../../services/reports-base.service';
import { AfricaMapComponent } from '../../africa-map/africa-map.component';
import { ChartJsComponent } from '../../shared/chart-js/chart-js.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TooltipService } from '../../services/tooltip.service';

export type MpiloLearningPathId = 'branchManager' | 'callCenterAgent';

export interface MpiloLearningImpactView {
  usersBeganCourseTotal: number;
  usersBeganCourseChangeClass: string;
  usersBeganCourseChangeIcon: string;
  usersBeganCourseChangePercentage: string;
  courseCompletionsTotal: number;
  courseCompletionsChangeClass: string;
  courseCompletionsChangeIcon: string;
  courseCompletionsChangePercentage: string;
  completionRateTotal: number;
  completionRateChangeClass: string;
  completionRateChangeIcon: string;
  completionRateChangePercentage: string;
  prePostChart: any;
  prePostIndex: { quiz: string; color: string }[];
  prePostOptions: any;
  currentPeriodStats: QuizPerformanceResponse[];
  preQuizChart: any;
  postQuizChart: any;
  engagementChart: any;
  dropOffChart: any;
  financeTable: CompletionTableData[];
  mAndEResponses: MAndEData[];
  mAndEChart: any;
  mAndEConfig: MandEConfigModel[];
  avgImprovement: number;
  moodAnalytics: MoodAnalyticsData[];
  moodChart: any;
  moodDistinctCount: number;
  moodTotalNumber: number;
  monthlyMoodActivityChart: any;
}

@Component({
  selector: 'app-mpilo-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatDialogModule,
    MatTableModule,
    IgxCategoryChartModule,
    IgxPieChartModule,
    IgxItemLegendModule,
    IgxLegendModule,
    MatTabsModule,
    MatCheckboxModule,
    MatTooltipModule,
    AfricaMapComponent,
    ChartJsComponent,
  ],
  templateUrl: './mpilo-report.component.html',
  styleUrl: './mpilo-report.component.scss',
})
export class MpiloReportComponent implements OnInit {

  personalFinanceTableData: CompletionTableData[] = [];
  trend: { percentage: number; color: string } | null = null;

  branchManagerPrePostQuizChartOptions: any = {
    indexAxis: 'y',
    plugins: {
      legend: { display: true, position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const val = context.parsed.x;
            return `${label}: ${val}%`;
          }
        }
      }
    },
    scales: {
      x: { beginAtZero: true, max: 100, ticks: { callback: (value: any) => value + '%' } },
      y: {
        ticks: {
          autoSkip: false,
          callback: (value: any, index: any) => {
            const config = this.baseService.prePostQuizQuestionsConfig || {};
            const customLabels = config['mpilo_path1'] || [];
            const rawLabel = this.baseService.mpiloPath1PrePostChartData?.labels?.[index] as string || '';
            return customLabels[index] || rawLabel || value;
          }
        }
      }
    }
  };

  callCenterAgentPrePostQuizChartOptions: any = {
    indexAxis: 'y',
    plugins: {
      legend: { display: true, position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const val = context.parsed.x;
            return `${label}: ${val}%`;
          }
        }
      }
    },
    scales: {
      x: { beginAtZero: true, max: 100, ticks: { callback: (value: any) => value + '%' } },
      y: {
        ticks: {
          autoSkip: false,
          callback: (value: any, index: any) => {
            const config = this.baseService.prePostQuizQuestionsConfig || {};
            const customLabels = config['mpilo_path2'] || [];
            const rawLabel = this.baseService.mpiloPath2PrePostChartData?.labels?.[index] as string || '';
            return customLabels[index] || rawLabel || value;
          }
        }
      }
    }
  };

  constructor(
    private reportsService: ReportsIntergrationService,
    private authService: KeycloakAuthService,
    private helperService: HttpHelperService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    public baseService: ReportsBaseService,
    public nativeElement: ElementRef,
    public tooltips: TooltipService,
  ) { }

  ngOnInit(): void {
    this.baseService.updateDateRange();
    this.checkUserBotAccess();
  }

  openDateRangeDialog(): void {
    const dialogRef = this.dialog.open(DateRangeDialogComponent, {
      data: { startDate: this.baseService.startDate, endDate: this.baseService.endDate }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.baseService.startDate = result.startDate;
        this.baseService.endDate = result.endDate;
        this.onDateChange();
      }
    });
  }

  getPercentage(count: number): string {
    const total = this.baseService.countryData.reduce((sum, country) => sum + country.count, 0);
    return total === 0 ? '0' : ((count / total) * 100).toFixed(2);
  }

  private checkUserBotAccess(): void {
    this.baseService.isLoading = true;
    this.baseService.error = null;

    const user = this.authService.currentUserValue();
    if (user && user.roles) {
      this.processUserBotAccess(user);
      return;
    }

    this.authService.currentUser$
      .pipe(
        filter((u: any) => !!u && !!u.roles),
        take(1)
      )
      .subscribe({
        next: (u: any) => {
          this.processUserBotAccess(u);
        },
        error: (err) => {
          console.error('Error fetching user data:', err);
          this.baseService.error = "Failed to load user data. Please try logging in again.";
          this.baseService.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private processUserBotAccess(user: any): void {
    const botRoles = user.roles.filter((role: string) => role.startsWith('bot_access_'));
    this.baseService.availableBots = botRoles.map((role: string) => {
      const botName = role.replace('bot_access_', '');
      return {
        key: botName as 'lwazi' | 'lesedi' | 'kitso' | 'kitsoecd' | 'kitsozazi' | 'kitsoai' | 'mpilo',
        name: botName.charAt(0).toUpperCase() + botName.slice(1)
      };
    });

    this.baseService.availableBots = this.baseService.availableBots.filter(bot => bot.key === 'mpilo');

    if (this.baseService.availableBots.length > 0) {
      this.baseService.selectedBot = 'mpilo';
      this.loadAllData();
    } else {
      this.baseService.error = "You do not have access to Mpilo bot. Please contact an administrator.";
    }
    this.baseService.isLoading = false;
    this.cdr.detectChanges();
  }

  onDateChange(): void {
    this.baseService.clearData();
    this.baseService.updateDateRange();
    if (this.baseService.selectedBot) {
      this.loadAllData();
    }
  }

  onProvinceFilterChange(): void {
    this.baseService.updateProvinceChartForFilter();
    this.cdr.detectChanges();
  }

  onLanguageFilterChange(): void {
    this.baseService.updateLanguageChartForFilter();
    this.cdr.detectChanges();
  }

  resolveMpiloLearningImpactView(pathway: MpiloLearningPathId): MpiloLearningImpactView {
    const reportsBaseService = this.baseService;
    if (pathway === 'branchManager') {
      return {
        usersBeganCourseTotal: reportsBaseService.mpiloPath1UsersBeganCourseTotal,
        usersBeganCourseChangeClass: reportsBaseService.mpiloPath1UsersBeganCourseChangeClass,
        usersBeganCourseChangeIcon: reportsBaseService.mpiloPath1UsersBeganCourseChangeIcon,
        usersBeganCourseChangePercentage: reportsBaseService.mpiloPath1UsersBeganCourseChangePercentage,
        courseCompletionsTotal: reportsBaseService.mpiloPath1CourseCompletionsTotal,
        courseCompletionsChangeClass: reportsBaseService.mpiloPath1CourseCompletionsChangeClass,
        courseCompletionsChangeIcon: reportsBaseService.mpiloPath1CourseCompletionsChangeIcon,
        courseCompletionsChangePercentage: reportsBaseService.mpiloPath1CourseCompletionsChangePercentage,
        completionRateTotal: reportsBaseService.mpiloPath1CompletionRateTotal,
        completionRateChangeClass: reportsBaseService.mpiloPath1CompletionRateChangeClass,
        completionRateChangeIcon: reportsBaseService.mpiloPath1CompletionRateChangeIcon,
        completionRateChangePercentage: reportsBaseService.mpiloPath1CompletionRateChangePercentage,
        prePostChart: reportsBaseService.mpiloPath1PrePostChartData,
        prePostIndex: reportsBaseService.mpiloPath1PrePostQuizQuestionsIndexData,
        prePostOptions: this.branchManagerPrePostQuizChartOptions,
        currentPeriodStats: reportsBaseService.mpiloPath1CurrentPeriodStats,
        preQuizChart: reportsBaseService.mpiloPath1PreQuizScoresChartData,
        postQuizChart: reportsBaseService.mpiloPath1PostQuizScoresChartData,
        engagementChart: reportsBaseService.mpiloPath1OverAllEngagementChartData,
        dropOffChart: reportsBaseService.mpiloPath1DropOffChartData,
        financeTable: reportsBaseService.personalFinanceTableData,
        mAndEResponses: reportsBaseService.mpiloPath1MAndEResponses,
        mAndEChart: reportsBaseService.mpiloPath1MAndEChart,
        mAndEConfig: reportsBaseService.mpiloPath1MAndEConfigArray,
        avgImprovement: reportsBaseService.getAverageImprovement(reportsBaseService.mpiloPath1CurrentPeriodStats),
        moodAnalytics: reportsBaseService.mpiloPath1MoodAnalytics,
        moodChart: reportsBaseService.mpiloPath1MoodChartData,
        moodDistinctCount: reportsBaseService.mpiloPath1MoodDistinctCount,
        moodTotalNumber: reportsBaseService.mpiloPath1MoodTotalNumber,
        monthlyMoodActivityChart: reportsBaseService.mpiloPath1MonthlyMoodActivityChartData,
      };
    }
    return {
      usersBeganCourseTotal: reportsBaseService.mpiloPath2UsersBeganCourseTotal,
      usersBeganCourseChangeClass: reportsBaseService.mpiloPath2UsersBeganCourseChangeClass,
      usersBeganCourseChangeIcon: reportsBaseService.mpiloPath2UsersBeganCourseChangeIcon,
      usersBeganCourseChangePercentage: reportsBaseService.mpiloPath2UsersBeganCourseChangePercentage,
      courseCompletionsTotal: reportsBaseService.mpiloPath2CourseCompletionsTotal,
      courseCompletionsChangeClass: reportsBaseService.mpiloPath2CourseCompletionsChangeClass,
      courseCompletionsChangeIcon: reportsBaseService.mpiloPath2CourseCompletionsChangeIcon,
      courseCompletionsChangePercentage: reportsBaseService.mpiloPath2CourseCompletionsChangePercentage,
      completionRateTotal: reportsBaseService.mpiloPath2CompletionRateTotal,
      completionRateChangeClass: reportsBaseService.mpiloPath2CompletionRateChangeClass,
      completionRateChangeIcon: reportsBaseService.mpiloPath2CompletionRateChangeIcon,
      completionRateChangePercentage: reportsBaseService.mpiloPath2CompletionRateChangePercentage,
      prePostChart: reportsBaseService.mpiloPath2PrePostChartData,
      prePostIndex: reportsBaseService.mpiloPath2PrePostQuizQuestionsIndexData,
      prePostOptions: this.callCenterAgentPrePostQuizChartOptions,
      currentPeriodStats: reportsBaseService.mpiloPath2CurrentPeriodStats,
      preQuizChart: reportsBaseService.mpiloPath2PreQuizScoresChartData,
      postQuizChart: reportsBaseService.mpiloPath2PostQuizScoresChartData,
      engagementChart: reportsBaseService.mpiloPath2OverAllEngagementChartData,
      dropOffChart: reportsBaseService.mpiloPath2DropOffChartData,
      financeTable: reportsBaseService.businessFinanceTableData,
      mAndEResponses: reportsBaseService.mpiloPath2MAndEResponses,
      mAndEChart: reportsBaseService.mpiloPath2MAndEChart,
      mAndEConfig: reportsBaseService.mpiloPath2MAndEConfigArray,
      avgImprovement: reportsBaseService.getAverageImprovement(reportsBaseService.mpiloPath2CurrentPeriodStats),
      moodAnalytics: reportsBaseService.mpiloPath2MoodAnalytics,
      moodChart: reportsBaseService.mpiloPath2MoodChartData,
      moodDistinctCount: reportsBaseService.mpiloPath2MoodDistinctCount,
      moodTotalNumber: reportsBaseService.mpiloPath2MoodTotalNumber,
      monthlyMoodActivityChart: reportsBaseService.mpiloPath2MonthlyMoodActivityChartData,
    };
  }

  private generateMpiloPrePostChartConfig(stats: QuizPerformanceResponse[] | null | undefined) {
    if (!stats || stats.length === 0) return null;
    const questions = stats.map(q => q.quizQuestion);
    const preData = stats.map(q => parseFloat(q.pre_learning_percentage) || 0);
    const postData = stats.map(q => parseFloat(q.post_learning_percentage) || 0);
    const preColor = 'rgba(235, 182, 6, 1)';
    const postColor = 'rgba(62, 132, 220, 1)';
    return {
      labels: questions,
      datasets: [
        {
          label: 'Pre-Learning',
          data: preData,
          backgroundColor: Array(preData.length).fill(preColor),
          borderColor: Array(preData.length).fill(preColor),
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
          barThickness: this.baseService.prePostQuizBarThicknessPx,
        },
        {
          label: 'Post-Learning',
          data: postData,
          backgroundColor: Array(postData.length).fill(postColor),
          borderColor: Array(postData.length).fill(postColor),
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
          barThickness: this.baseService.prePostQuizBarThicknessPx,
        }
      ]
    };
  }

  private buildPrePostQuizChartData() {
    const preColor = 'rgba(235, 182, 6, 1)';
    const postColor = 'rgba(62, 132, 220, 1)';
    const legend = [
      { quiz: 'Pre-Learning', color: preColor },
      { quiz: 'Post-Learning', color: postColor },
    ];

    this.baseService.mpiloPath1PrePostChartData = this.generateMpiloPrePostChartConfig(
      this.baseService.mpiloPath1CurrentPeriodStats
    );
    this.baseService.mpiloPath1PrePostQuizQuestionsIndexData = this.baseService.mpiloPath1PrePostChartData
      ? legend
      : [];

    this.baseService.mpiloPath2PrePostChartData = this.generateMpiloPrePostChartConfig(
      this.baseService.mpiloPath2CurrentPeriodStats
    );
    this.baseService.mpiloPath2PrePostQuizQuestionsIndexData = this.baseService.mpiloPath2PrePostChartData
      ? legend
      : [];
  }

  private createDoughnutConfig(value: number, colorKey: 'preQuiz' | 'postQuiz' | 'overAllEngagement', label: string) {
    const tone = this.baseService.pieChartColor[colorKey];
    const fill = Array.isArray(tone) ? tone[0] : tone;
    return {
      labels: [label, 'Remaining'],
      datasets: [{
        data: [value, 100 - value],
        backgroundColor: [fill, '#E0E0E0'],
        label
      }]
    };
  }

  private buildMpiloAssessmentCharts(): void {
    const reportsBaseService = this.baseService;

    const buildPathwayAssessmentCharts = (stats: QuizPerformanceResponse[], beganCourse: number) => {
      const pre = reportsBaseService.getAveragePreQuizScore(stats);
      const post = reportsBaseService.getAveragePostQuizScore(stats);
      const drop = reportsBaseService.calculateDropOffStats(stats, beganCourse);
      const engagement = reportsBaseService.getOverallEngagementStats(beganCourse, reportsBaseService.enrolledUsersTotal);
      return {
        preQuiz: this.createDoughnutConfig(pre, 'preQuiz', 'Average Pre-Quiz Score'),
        postQuiz: this.createDoughnutConfig(post, 'postQuiz', 'Average Post-Quiz Score'),
        engagement: this.createDoughnutConfig(engagement, 'overAllEngagement', 'Average Engagement Score'),
        dropOff: this.createDoughnutConfig(drop, 'overAllEngagement', 'Drop-Off Percentage'),
      };
    };

    const branchManagerAssessmentCharts = buildPathwayAssessmentCharts(
      reportsBaseService.mpiloPath1CurrentPeriodStats,
      reportsBaseService.mpiloPath1UsersBeganCourseTotal
    );
    reportsBaseService.mpiloPath1PreQuizScoresChartData = branchManagerAssessmentCharts.preQuiz;
    reportsBaseService.mpiloPath1PostQuizScoresChartData = branchManagerAssessmentCharts.postQuiz;
    reportsBaseService.mpiloPath1OverAllEngagementChartData = branchManagerAssessmentCharts.engagement;
    reportsBaseService.mpiloPath1DropOffChartData = branchManagerAssessmentCharts.dropOff;

    const callCenterAgentAssessmentCharts = buildPathwayAssessmentCharts(
      reportsBaseService.mpiloPath2CurrentPeriodStats,
      reportsBaseService.mpiloPath2UsersBeganCourseTotal
    );
    reportsBaseService.mpiloPath2PreQuizScoresChartData = callCenterAgentAssessmentCharts.preQuiz;
    reportsBaseService.mpiloPath2PostQuizScoresChartData = callCenterAgentAssessmentCharts.postQuiz;
    reportsBaseService.mpiloPath2OverAllEngagementChartData = callCenterAgentAssessmentCharts.engagement;
    reportsBaseService.mpiloPath2DropOffChartData = callCenterAgentAssessmentCharts.dropOff;
  }

  private buildMpiloMAndEChartData(pathway: MpiloLearningPathId): void {
    const rawData =
      pathway === 'branchManager'
        ? this.baseService.mpiloPath1MAndEAverages
        : this.baseService.mpiloPath2MAndEAverages;
    const chartConfig = this.baseService.buildFeedbackRatingOverTimeChartData(rawData);
    if (pathway === 'branchManager') {
      this.baseService.mpiloPath1MAndEChart = chartConfig;
    } else {
      this.baseService.mpiloPath2MAndEChart = chartConfig;
    }
  }

  private loadAllData(): Promise<void> {
    const localBot = this.baseService.selectedBot;
    if (!localBot) return Promise.resolve();

    this.baseService.isLoading = true;
    const currentStart = this.baseService.formatDate(this.baseService.startDate);
    const currentEnd = this.baseService.formatDate(this.baseService.endDate);
    const { prevStart, prevEnd } = this.baseService.getPreviousPeriod(this.baseService.startDate, this.baseService.endDate);

    const quizStats = this.baseService.loadPrePostQuizStats(localBot, currentStart, currentEnd);
    const engagement = this.baseService.loadEngagementCount(localBot, currentStart, currentEnd, prevStart, prevEnd);
    const completion = this.baseService.loadCompletionStats(localBot, currentStart, currentEnd, prevStart, prevEnd);
    const mAndEStats = this.baseService.loadMAndEStats(localBot, currentStart, currentEnd);
    const mAndENumber = this.baseService.loadMAndENumber(localBot, currentStart, currentEnd);
    const moodAnalytics = this.baseService.loadMoodAnalytics(localBot, currentStart, currentEnd);
    const monthlyMoodActivity = this.baseService.loadMonthlyMoodActivity(localBot, currentStart, currentEnd);

    return Promise.all([
      this.baseService.loadRemainingData(currentStart, currentEnd),
      this.baseService.loadBreakdowns(localBot, currentStart, currentEnd),
      this.baseService.loadTables(localBot, currentStart, currentEnd, prevStart, prevEnd),
      moodAnalytics.then(() => this.cdr.detectChanges()),
      monthlyMoodActivity.then(() => this.cdr.detectChanges()),

      quizStats.then(() => {
        this.buildPrePostQuizChartData();
        this.cdr.detectChanges();
      }),

      Promise.all([quizStats, engagement, completion]).then(() => {
        this.buildMpiloAssessmentCharts();
        this.cdr.detectChanges();
      }),

      Promise.all([mAndEStats, mAndENumber]).then(() => {
        this.baseService.updateMAndEConfig();
        this.buildMpiloMAndEChartData('branchManager');
        this.buildMpiloMAndEChartData('callCenterAgent');
        this.cdr.detectChanges();
      }),
    ])
    .then(() => {
      this.baseService.isLoading = false;
      this.cdr.markForCheck();
    })
    .catch((error) => {
      console.error('Error in loadAllData chain:', error);
      this.baseService.isLoading = false;
      this.baseService.error = 'Failed to load report data';
      this.cdr.detectChanges();
    });
  }

  private clearData(): void {
    this.baseService.clearData();
  }
}
